import { CircularProgress, MenuItem, TextField } from '@material-ui/core';
import {
  CalendarTodayOutlined,
  EventAvailableOutlined,
  ReportProblemOutlined,
} from '@material-ui/icons';
import React, { useContext } from 'react';
import { AppContext } from '../../../app/AppContext';
import { TODAY } from '../../../utils/constants';
import { AVAILABLE_HOURS } from '../../../utils/dates';

const SessionSummary = ({
  meetingList,
  setMeetingList,
  bussyDates = [],
  isCheckingSchedules = false,
  isBusy = false,
}) => {
  const { user } = useContext(AppContext);
  const isAdmin = user?.role?.type === 'admin';

  const updateMeeting = (meetingIndex, changes) => {
    if (isBusy || isCheckingSchedules) return;

    const newList = meetingList.map((meeting, index) => {
      if (index !== meetingIndex) return meeting;

      const updatedMeeting = { ...meeting, ...changes };
      const hour = String(updatedMeeting.time).split(':')[0].padStart(2, '0');
      const busySlot = bussyDates.find((slot) =>
        slot.date.includes(`${updatedMeeting.date} ${hour}:00`)
      );

      if (busySlot) {
        updatedMeeting.msg = busySlot.message;
      } else {
        delete updatedMeeting.msg;
      }

      return updatedMeeting;
    });

    setMeetingList(newList);
  };

  return (
    <aside
      className="session-scheduler__summary session-scheduler__card"
      aria-labelledby="session-scheduler-summary-title"
      aria-busy={isCheckingSchedules || isBusy}
    >
      <header className="session-scheduler__card-header">
        <span className="session-scheduler__section-icon" aria-hidden="true">
          <EventAvailableOutlined />
        </span>
        <div>
          <span className="session-scheduler__eyebrow">Revisa tu agenda</span>
          <h2 id="session-scheduler-summary-title">Horarios programados</h2>
        </div>
        <span
          className="session-scheduler__count"
          aria-label={`${meetingList.length} sesiones programadas`}
        >
          {meetingList.length}
        </span>
      </header>

      {isCheckingSchedules ? (
        <div className="session-scheduler__empty" role="status">
          <span className="session-scheduler__empty-icon" aria-hidden="true">
            <CircularProgress size={28} />
          </span>
          <h3>Consultando disponibilidad</h3>
          <p>Estamos revisando los horarios de ambos participantes.</p>
        </div>
      ) : meetingList.length ? (
        <>
          <p className="session-scheduler__hint">
            Revisa las sesiones antes de confirmarlas. Puedes ajustar la fecha y
            hora de cada una.
          </p>
          <ol className="session-scheduler__summary-list">
            {meetingList.map((meeting, index) => {
              const dateId = `session-scheduler-date-${index}`;
              const timeId = `session-scheduler-time-${index}`;
              const warningId = `session-scheduler-warning-${index}`;

              return (
                <li
                  key={`meeting-${index}`}
                  className={`session-scheduler__meeting${
                    meeting.msg ? ' session-scheduler__meeting--conflict' : ''
                  }`}
                >
                  <div className="session-scheduler__meeting-header">
                    <span
                      className="session-scheduler__meeting-number"
                      aria-hidden="true"
                    >
                      {index + 1}
                    </span>
                    <h3>Sesión {index + 1}</h3>
                  </div>
                  <div className="session-scheduler__meeting-fields">
                    <div className="session-scheduler__field">
                      <label className="session-scheduler__field-label" htmlFor={dateId}>
                        Fecha
                      </label>
                      <TextField
                        id={dateId}
                        name={`date-${index}`}
                        type="date"
                        variant="outlined"
                        fullWidth
                        value={meeting.date}
                        disabled={isBusy}
                        error={Boolean(meeting.msg)}
                        inputProps={{
                          'aria-label': `Fecha de la sesión ${index + 1}`,
                          'aria-describedby': meeting.msg ? warningId : undefined,
                          ...(isAdmin ? {} : { min: TODAY }),
                        }}
                        onChange={({ target }) =>
                          updateMeeting(index, { date: target.value })
                        }
                      />
                    </div>
                    <div className="session-scheduler__field">
                      <label className="session-scheduler__field-label" htmlFor={timeId}>
                        Hora
                      </label>
                      <TextField
                        id={timeId}
                        name={`startTime-${index + 1}`}
                        select
                        variant="outlined"
                        fullWidth
                        value={meeting.time}
                        disabled={isBusy}
                        error={Boolean(meeting.msg)}
                        SelectProps={{
                          SelectDisplayProps: {
                            'aria-label': `Hora de la sesión ${index + 1}`,
                            'aria-describedby': meeting.msg ? warningId : undefined,
                          },
                          MenuProps: {
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            getContentAnchorEl: null,
                            PaperProps: { className: 'session-scheduler-options' },
                          },
                        }}
                        onChange={({ target }) =>
                          updateMeeting(index, { time: target.value })
                        }
                      >
                        {AVAILABLE_HOURS.map((option) => (
                          <MenuItem
                            className="session-scheduler-option"
                            key={option.id}
                            value={option.id}
                          >
                            {option.value}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                  </div>
                  {meeting.msg && (
                    <p
                      id={warningId}
                      className="session-scheduler__warning"
                      role="alert"
                    >
                      <ReportProblemOutlined aria-hidden="true" />
                      <span>{meeting.msg}</span>
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      ) : (
        <div className="session-scheduler__empty" role="status">
          <span className="session-scheduler__empty-icon" aria-hidden="true">
            <CalendarTodayOutlined />
          </span>
          <h3>Aún no hay horarios</h3>
          <p>
            Completa la programación y pulsa «Ver horarios» para revisar las
            sesiones que vas a crear.
          </p>
        </div>
      )}
    </aside>
  );
};

export default SessionSummary;
